/*
Usage Notes: 

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  {% assign skioSellingPlanGroups = product.selling_plan_groups | where: 'app_id', 'SKIO' %}
  {% if skioSellingPlanGroups.size > 0 %}
    <skio-plan-picker 
      product='{{ product | json | escape }}'
      selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
      formId='{{ 'product-form-' | append: section.id }}'
      externalPriceSelector='#price-inner-button .money'
      externalPriceSelectorWithCurrency='[data-product-price-list] .money'
      currency='USD'
      useVariantInputClickEvents='false'
      variantInputSelector=''
      >
    </skio-plan-picker>
    <input type="hidden" aria-hidden="true" name="selling_plan" value="">
    <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>
    {% render 'skio-details' %}
  {% endif %}

  Note: formId not required if element is inside of a form already

  Example variantChanged event dispatch:
  document.dispatchEvent( new CustomEvent("variantChanged", { detail: { variantId: variant.id } }) );
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0;
    border: 0;
    margin: 20px 0 0;
  }
  .skio-onetime-second {
    order: 2;
  }

  .skio-group-container {
    display: none;
  }

  .skio-group-container--available {
    display: block;
    position: relative;
    color: rgba(0, 0, 0, 0.75);
    border-radius: 5px;
    border: 1px solid #e8eaeb;
    background: rgba(1, 65, 94, 0.01);
  }

  .skio-group-container--selected {
    color: #01415e;
    background: rgba(1, 65, 94, 0.03);
    border: 1px solid #01415e;
  }

  .skio-group-container--available .skio-radio__container {
    color: #01415e;
  }

  .skio-group-container--selected .skio-radio__container {
    color: #01415e;
  }

  .skio-group-container--selected .skio-group-title {
    color: #01415e;
  }

  .skio-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 2px #ccc solid;
    outline-offset: 4px;
    border-radius: 5px;
  }

  .skio-group-label {
    display: flex;
    cursor: pointer;
    padding: 1.8rem 2.6rem;
    overflow: hidden;
  }

  .skio-group-topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    font-size: 16px;
  }

  .skio-radio__container {
    display: flex;
    margin-right: 10px;
  }

  .skio-radio__container svg {
    stroke-opacity: 0.2;
  }

  .skio-group-container--selected .skio-radio__container svg {
    stroke-opacity: 1;
  }

  .skio-radio__container svg circle + circle {
    fill: transparent;
  }

  .skio-group-container--selected .skio-radio__container svg circle + circle {
    fill: #01415e;
  }

  .skio-radio {
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: center;
    transform: scale(0);
    opacity: 0;
  }
  .skio-group-label:hover .skio-radio {
    transform: scale(1);
    opacity: 0.75;
  }
  .skio-group-container--selected .skio-group-label .skio-radio {
    transform: scale(1);
    opacity: 1;
  }

  .skio-price {
    margin-left: auto;
    display: flex;
    flex-direction: row-reverse;
    column-gap: 4px;
    color: rgba(1, 65, 94, 0.5);
    text-align: center;
    font-family: ABCMonumentGroteskRegular;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 25.02px;
  }

  .--isCondensed .skio-price {
    font-size: 16px;
    line-height: 22px;
  }

  .skio-onetime-price {
    font-size: 12px;
    color: rgba(1, 65, 94, 0.5);
    text-align: center;
    font-family: ABCMonumentGroteskRegular;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 25.02px;
    text-decoration: line-through;
  }

  .skio-compare-price {
    font-size: 18px;
    color: rgba(1, 65, 94, 0.5);
    text-align: center;
    font-family: ABCMonumentGroteskRegular;
    font-style: normal;
    font-weight: 400;
    line-height: 25.02px;
    text-decoration: line-through;
  }

  .--isCondensed .skio-onetime-price {
    font-size: 16px;
    line-height: 22px;
  }

  .--isCondensed .skio-compare-price {
    font-size: 16px;
    line-height: 22px;
  }

  .skio-group-content {
    width: auto;
    opacity: 1;
    order: 3;
  }

  .skio-group-container--selected .skio-price {
    color: rgba(1, 65, 94, 1);
  }

  .skio-group-content label {
    font-weight: bold;
  }

  /* Hide frequency if not selected */
  .skio-group-container:not(.skio-group-container--selected) + .skio-group-content {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
  }

  .skio-group-title {
    font-family: ABCMonumentGroteskRegular;
    font-size: 18px;
    line-height: 25px;
    font-weight: 400;
    color: #01415e;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - 30px);
    letter-spacing: normal;
  }

  .skio-group-title svg {
    margin-left: 5px;
  }

  /* .skio-save {
    color: #0fa573;
    border: 1px #0fa573 solid; 
    padding: 0px 8px;
    border-radius: 20px;
  } */

  .skio-frequency {
    display: inline-flex;
    padding: 7px 35px 7px 11px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    color: #01415e !important;
    border-radius: 3.6px;
    border: 1px solid #01415e;
    background-color: rgba(1, 65, 94, 0.05);
    text-transform: capitalize;
    text-decoration: none;
    font-size: 14px;
    line-height: 19.46px;
    font-family: ABCMonumentGroteskRegular;
    white-space: nowrap;
    outline: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 10px top 50%;
    background-size: 18px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }

  .skio-frequency.skio-frequency--one {
    background-image: none;
    pointer-events: none;
  }

  .skio-frequency span {
    text-transform: lowercase;
  }

  .flex {
    display: flex;
  }

  .sub-benefit-container {
    padding: 0 26px 15px;
  }

  .sub-benefit-list {
    font-family: ABCArizonaSerifLight;
    color: inherit;
    padding: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 0 0 12px;
    row-gap: 5px;
    border-top: 1px solid rgba(1, 65, 94, 0.2);
    padding-top: 15px;
  }

  .sub-benefit-list li {
    list-style-type: none;
    color: #01415e;
    font-family: ABCArizonaSerifLight;
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 32px;
    display: flex;
    column-gap: 12px;
    align-items: center;
  }

  .sub-benefit-frequency {
    display: flex;
    flex-wrap: nowrap;
    column-gap: 9px;
  }

  .skio-subscription-price-specialOfferText {
    padding: 2px 4px;
    font-size: 10px;
    line-height: 12px;
    font-weight: 700;
    background-color: #5e8092;
    color: #ffffff;
    border: none;
    display: flex;
    align-items: center;
    font-family: ABCMonumentGroteskMedium;
    white-space: nowrap;
    margin-left: auto;
  }

  .highlighted-tag {
    background: #01415e;
    width: 100%;
    display: block;
    box-sizing: border-box;
    color: #ffffff;
    padding: 5px 10px;
    text-transform: inherit;
    letter-spacing: normal;
    font-family: ABCMonumentGroteskRegular;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-align: center;
  }

  .skio-save.--hide {
    display: none;
  }

  .--isCondensed .skio-group-container--subscribe {
    padding: 0;
  }

  .sub-benefit-container.--isCondensed .sub-benefit-list li:nth-child(n + 4) {
    display: none;
  }

  .--isCondensed .sub-benefit-list li {
    font-size: 14px;
    line-height: 23px;
    column-gap: 6px;
    flex: 1 1 auto;
  }

  .--isCondensed .sub-benefit-list {
    flex-wrap: nowrap;
    column-gap: 13px;
    display: flex;
  }

  .--isCondensed .skio-group-content {
    text-align: right;
  }

  .--isCondensed .highlighted-tag {
    display: none;
  }

  @media (max-width: 1399px) {
    .skio-group-label {
      padding: 1.3rem 0.9rem 1.3rem 1.5rem;
    }
  }

  @media (max-width: 1024px) {
    .skio-onetime-price,
    .skio-compare-price,
    .skio-price,
    .skio-group-title {
      font-size: 18px;
      line-height: 22px;
    }
    .highlighted-tag {
      font-size: 11px;
    }
    .skio-frequency {
      font-size: 16px;
      line-height: 16px;
    }

    .sub-benefit-list li {
      font-size: 16px;
      line-height: 24px;
    }
  }

  @media (max-width: 749px) {
    .skio-group-label {
      padding: 1.4rem 0.9rem;
    }

    .sub-benefit-container {
      padding: 0 1.5rem 1.5rem;
    }

    .skio-frequency {
      width: 100%;
      text-align: center;
      border-radius: 3.21px;
      padding: 6px 11px;
    }

    .sub-benefit-list li {
      column-gap: 1rem;
      font-size: 16px;
    }
    .sub-benefit-list {
      row-gap: 0.7rem;
    }

    .sub-benefit-list li svg {
      width: 15.4px;
      height: 15.4px;
    }

    .skio-radio__container svg {
      width: 15.2px;
      height: 15.2px;
    }

    .--isCondensed .sub-benefit-list li {
      font-size: 12px;
    }
  }
  @media (max-width: 410px) {
    .skio-onetime-price,
    .skio-compare-price,
    .skio-price,
    .skio-group-title {
      font-size: 15px;
    }

    .sub-benefit-list li {
      font-size: 14px;
    }

    .skio-frequency {
      font-size: 14px;
    }
    .--isCondensed .sub-benefit-list li {
      font-size: 10px;
    }
  }
`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: {
      type: Object,
      attribute: 'product',
      converter: {
        fromAttribute: (value) => {
          try {
            return value ? JSON.parse(value) : null;
          } catch (e) {
            return null;
          }
        },
      },
    }, //required
    productHandle: { type: String }, //optional (unless product isn't passed, then required)
    key: { type: String }, //optional, defaults to product.id; identifier for this instance of the Skio plan picker

    formId: { type: String }, //optional; if passed, used to connect input fields to form
    needsFormId: { type: Boolean }, //optional, defaults to false; if true, element needs to be passed a formId, else it searches for a form

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    discountFormat: { type: String }, //optional, defaults to percent; can also pass "fixed"

    currency: { type: String }, //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String }, //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {}, //placeholder for object

    externalPriceSelector: { type: String }, //optional, used to update the external price

    externalPriceSelectorWithCurrency: { type: String }, //optional, used to update the external price

    selectedVariant: {
      type: Object,
      attribute: 'selectedvariant',
      converter: {
        fromAttribute: (value) => {
          try {
            return value ? JSON.parse(value) : null;
          } catch (e) {
            return null;
          }
        },
      },
    }, //placeholder for data
    skioSellingPlanGroups: {}, //placeholder for data
    availableSellingPlanGroups: {}, //placeholder for data
    selectedSellingPlanGroup: {}, //placeholder for data
    selectedSellingPlan: {}, //placeholder for data

    defaultFrequency: {}, //placeholder for data

    useVariantInputClickEvents: { type: Boolean }, // optional, allows use of variant input click events to update skio's selectedVariant
    variantInputSelector: {},
    highlightedTag: { type: String }, // optional, property for dynamic highlighted text
    highlightedTagBgColor: { type: String }, // optional,  property for dynamic highlighted background color
    subsPriceColor: { type: String }, // optional, property for subscription price color
    specialOfferText: { type: String }, // optional, property for dynamic special offer text
    specialOfferColor: { type: String }, // optional, property for dynamic special offer text color
    specialOfferBgColor: { type: String }, // optional, property for dynamic special offer Bg color
    subscriptionTitle: { type: String }, // optional, property for dynamic Subscription Title
    oneTimeTitle: { type: String }, // optional, property for dynamic one time purchase
    hideDiscount: { type: String }, // optional, allow to hide discount % text
    isCondensed: { type: String }, // optional, allow to show condensed version of the plan picker
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.purchaseOption = 'onetime';

    this.key = null;
    this.formId = null;
    this.needsFormId = false;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = false;
    this.subscriptionFirst = false;

    this.skioMainProduct = true;

    this.discountFormat = 'percent';

    this.externalPriceSelector = '.price-item--regular bdi';

    this.externalPriceSelectorWithCurrency = '[skio-external-price-with-currency]';

    this.currency = Shopify.currency.active;
    this.language = 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });

    this.defaultFrequency = null;

    this.useVariantInputClickEvents = null;
    this.variantInputSelector = null;
    this.highlightedTag = 'Most Popular'; // or any default value
    this.highlightedTagBgColor = '#5E8092';
    this.subsPriceColor = '#5E8092';
    this.specialOfferText = '';
    this.specialOfferColor = '#05A6CC';
    this.specialOfferBgColor = '#E0F2F5';
    this.subscriptionTitle = 'Subscribe & Save';
    this.oneTimeTitle = 'One-Time Purchase';
    this.hideDiscount = 'false';
    this.isCondensed = 'false';
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.startSubscription == true) {
      this.purchaseOption = 'subscription';
    }

    if (!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    if (this.needsFormId && this.formId == null) {
      let forms = document.querySelectorAll('form[action="/cart/add"]');
      if (forms.length > 0) {
        let form;
        forms.forEach((el) => {
          if (el.hasAttribute('skio-key')) {
            if (el.getAttribute('skio-key') == this.key) form = el;
          }
        });
        if (!form) form = forms[0];
        this.formId = form.id;
        this.requestUpdate();
      }
    }
    let skio = this;
    document.addEventListener('variantChanged', function (e) {
      //update variant id
      let variantId = e.detail.variantId;
      let variant = skio.product.variants.find((x) => x.id == variantId);
      // Only update if the variant belongs to this product to prevent cross-section interference
      if (variant) {
        skio.selectedVariant = variant;
        skio.requestUpdate();
      }
      // Remove the else clause that was logging errors for variants from other products
    });
  }

  render() {
    if (!this.product || !this.selectedVariant || this.skioSellingPlanGroups.length == 0 || !this.product?.available)
      return;

    return html` <fieldset
      class="skio-plan-picker ${this.isCondensed == 'true' ? '--isCondensed' : ''}"
      skio-plan-picker="${this.key}"
    >
      <input
        ${this.formId !== null ? html`form="${this.formId}"` : ''}
        name="selling_plan"
        type="hidden"
        value="${this.selectedSellingPlan !== null ? this.selectedSellingPlan?.id : ''}"
      />
      <input
        ${this.formId !== null ? html`form="${this.formId}"` : ''}
        name="properties[Discount]"
        type="hidden"
        value="${this.selectedSellingPlan !== null
          ? this.calculateDynamicDiscount(this.selectedSellingPlan).percent
          : ''}"
        ?disabled="${this.selectedSellingPlan == null ? true : false}"
      />

      <div
        class="skio-group-container skio-group-container--one-time ${this.product.requires_selling_plan == false
          ? 'skio-group-container--available'
          : ''} ${this.selectedSellingPlanGroup == null ? 'skio-group-container--selected' : ''} ${this
          .subscriptionFirst
          ? 'skio-onetime-second'
          : ''}"
        skio-group-container
        @click=${() => this.selectSellingPlanGroup(null)}
      >
        <input
          id="skio-one-time-${this.key}"
          class="skio-group-input"
          name="skio-group-${this.key}"
          type="radio"
          value=""
          skio-one-time
          ?checked=${this.startSubscription == false && this.product.requires_selling_plan == false ? true : false}
        />
        <label class="skio-group-label" for="skio-one-time-${this.key}">
          <div class="skio-group-topline">
            <div class="skio-radio__container">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                focusable="false"
                aria-hidden="true"
              >
                <circle cx="9.5" cy="9.5" r="8.5" stroke="currentColor" stroke-width="2"></circle>
                <circle cx="9.5" cy="9.5" r="4.5" fill="currentColor"></circle>
              </svg>
            </div>
            <div class="skio-group-title">
              ${this.oneTimeTitle}${this.shouldShowComparePrice()
                ? html` (${this.discountFormat == 'percent'
                    ? this.calculateDynamicDiscount(null).percent
                    : this.calculateDynamicDiscount(null).amount}
                  Off)`
                : html``}
              <div class="skio-price">
                ${this.shouldShowComparePrice()
                  ? html`<span class="skio-compare-price">${this.compareAtPrice()}</span>`
                  : html``}

                <span skio-onetime-price>${this.moneyFormatter.format(this.selectedVariant.price / 100)}</span>
              </div>
            </div>
          </div>
        </label>
      </div>
      ${this.availableSellingPlanGroups
        ? this.availableSellingPlanGroups.map(
            (group, index) =>
              html`
                <div
                  class="skio-group-container skio-group-container--subscribe skio-group-container--available ${this
                    .selectedSellingPlanGroup == group
                    ? 'skio-group-container--selected'
                    : ''}"
                  skio-group-container
                  @click=${() => this.selectSellingPlanGroup(group)}
                >
                  ${this.highlightedTag
                    ? html`<span class="highlighted-tag" style="background: ${this.highlightedTagBgColor};">
                        ${this.highlightedTag}
                      </span>`
                    : html``}
                  <input
                    id="skio-selling-plan-group-${index}-${this.key}"
                    class="skio-group-input"
                    name="skio-group-${this.key}"
                    type="radio"
                    value="${group.id}"
                    skio-selling-plan-group="${group.id}"
                    ?checked=${this.selectedSellingPlanGroup == group ? true : false}
                  />
                  <label class="skio-group-label" for="skio-selling-plan-group-${index}-${this.key}">
                    <div class="skio-group-topline">
                      <div class="skio-radio__container">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="19"
                          height="19"
                          viewBox="0 0 19 19"
                          fill="none"
                          focusable="false"
                          aria-hidden="true"
                        >
                          <circle cx="9.5" cy="9.5" r="8.5" stroke="currentColor" stroke-width="2"></circle>
                          <circle cx="9.5" cy="9.5" r="4.5" fill="currentColor"></circle>
                        </svg>
                      </div>
                      <div class="skio-group-title">
                        ${this.subscriptionTitle.replace(/ \d+% Off$/, '')}${this.calculateDynamicDiscount(
                          group.selected_selling_plan
                        ).percent !== '0%'
                          ? html` (${this.discountFormat == 'percent'
                              ? this.calculateDynamicDiscount(group.selected_selling_plan).percent
                              : this.calculateDynamicDiscount(group.selected_selling_plan).amount}
                            Off)`
                          : html``}&nbsp;
                        ${this.calculateDynamicDiscount(group.selected_selling_plan).percent !== '0%'
                          ? html` <span class="skio-save --hide">
                                <span skio-discount
                                  >${this.discountFormat == 'percent'
                                    ? this.calculateDynamicDiscount(group.selected_selling_plan).percent
                                    : this.calculateDynamicDiscount(group.selected_selling_plan).amount}</span
                                ></span
                              >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="19"
                                height="19"
                                viewBox="0 0 19 19"
                                fill="none"
                              >
                                <path
                                  d="M16.8961 4.43156C16.8864 4.75608 16.8719 5.08027 16.8682 5.4048C16.8625 5.91227 17.0619 6.33096 17.4487 6.65516C17.6689 6.83978 17.905 7.00524 18.1286 7.18583C18.2659 7.29681 18.4034 7.41081 18.5235 7.53962C18.8029 7.83959 18.8746 8.1964 18.7269 8.57541C18.6422 8.79266 18.5174 8.99679 18.392 9.19487C18.2521 9.41582 18.0903 9.62264 17.9396 9.83686C17.5886 10.3356 17.522 10.8723 17.7281 11.4447C17.828 11.7218 17.9339 11.9969 18.0254 12.277C18.0732 12.423 18.1108 12.576 18.1266 12.7283C18.1653 13.1063 18.0163 13.4181 17.7083 13.6246C17.4692 13.785 17.1978 13.8993 16.9358 14.0234C16.7596 14.1068 16.5746 14.1717 16.3934 14.2437C15.8042 14.4778 15.4561 14.9126 15.3202 15.526C15.2621 15.7887 15.2029 16.051 15.1427 16.3129C15.1285 16.3738 15.1114 16.4343 15.0922 16.4939C14.8958 17.109 14.4617 17.3958 13.819 17.3144C13.476 17.271 13.1343 17.2075 12.796 17.1352C12.1331 16.9936 11.5618 17.1473 11.0836 17.6319C10.9877 17.7291 10.8915 17.8263 10.7923 17.9198C10.6285 18.0738 10.4718 18.2372 10.2949 18.3748C9.89206 18.6885 9.47303 18.7128 9.04392 18.4326C8.82633 18.2907 8.63498 18.1078 8.43824 17.9359C8.27144 17.7903 8.11641 17.6319 7.9523 17.4836C7.51948 17.0928 7.01201 16.9842 6.44905 17.0952C6.12015 17.1601 5.79293 17.2347 5.46269 17.2906C4.85702 17.3925 4.42555 17.1631 4.20393 16.5897C4.07983 16.2689 4.01358 15.9255 3.92076 15.5923C3.88411 15.4611 3.84947 15.3293 3.8081 15.1995C3.66888 14.7616 3.39479 14.434 2.97678 14.2444C2.65864 14.1001 2.3284 13.9824 2.00421 13.8512C1.91912 13.8166 1.83438 13.7793 1.75299 13.7369C1.25729 13.478 1.04273 13.062 1.16716 12.5168C1.25056 12.1523 1.38475 11.7988 1.49909 11.4417C1.60805 11.102 1.69347 10.7607 1.61881 10.3998C1.57005 10.1648 1.46882 9.95255 1.3343 9.75716C1.19205 9.55067 1.03567 9.35428 0.892744 9.14846C0.787819 8.99747 0.682221 8.84479 0.597811 8.68202C0.400741 8.30099 0.437733 7.92636 0.699373 7.59275C0.84129 7.41182 1.01886 7.25713 1.19238 7.10378C1.35246 6.96219 1.52431 6.83373 1.69717 6.70795C2.29847 6.27077 2.52076 5.67787 2.44946 4.94979C2.41752 4.62392 2.40205 4.29435 2.41348 3.96747C2.43029 3.48454 2.69698 3.15834 3.15401 3.03155C3.47786 2.94176 3.82055 2.91956 4.15516 2.87013C4.3593 2.83986 4.56545 2.82305 4.76823 2.78673C5.20138 2.70904 5.53936 2.477 5.77712 2.11077C5.93552 1.86696 6.07004 1.60801 6.21734 1.35713C6.292 1.23001 6.36363 1.10053 6.44905 0.980812C6.76853 0.532528 7.18419 0.386238 7.70781 0.558758C7.94759 0.637788 8.17392 0.758519 8.40394 0.866134C8.62758 0.970386 8.84449 1.0891 9.07015 1.18864C9.49523 1.37562 9.92602 1.36823 10.3461 1.17654C10.6104 1.05581 10.8619 0.907499 11.1229 0.779033C11.2974 0.693278 11.474 0.606177 11.6583 0.545643C12.039 0.42054 12.3884 0.504615 12.6662 0.787777C12.8347 0.959289 12.9719 1.16477 13.1044 1.36755C13.2634 1.61103 13.398 1.87032 13.5516 2.1175C13.8526 2.60176 14.3033 2.83953 14.8615 2.89266C15.2284 2.9273 15.5987 2.94344 15.9612 3.0033C16.5316 3.09747 16.8386 3.44856 16.8891 4.02363C16.9008 4.15882 16.8907 4.29637 16.8907 4.43257H16.8965L16.8961 4.43156Z"
                                  fill="#F15D22"
                                />
                                <path
                                  d="M6.09502 13.8739L5.50219 13.2811L13.464 5.33706L14.0568 5.92397L6.09502 13.8739ZM12.2783 14.0518C11.887 14.0518 11.5294 13.9569 11.2053 13.7672C10.8851 13.5735 10.6282 13.3166 10.4346 12.9965C10.2409 12.6724 10.1441 12.3128 10.1441 11.9175C10.1441 11.5302 10.2429 11.1745 10.4405 10.8504C10.6381 10.5264 10.899 10.2675 11.2231 10.0738C11.5471 9.88016 11.8989 9.78333 12.2783 9.78333C12.6696 9.78333 13.0273 9.88016 13.3513 10.0738C13.6754 10.2635 13.9323 10.5204 14.122 10.8445C14.3157 11.1646 14.4125 11.5223 14.4125 11.9175C14.4125 12.3128 14.3157 12.6724 14.122 12.9965C13.9323 13.3166 13.6754 13.5735 13.3513 13.7672C13.0273 13.9569 12.6696 14.0518 12.2783 14.0518ZM12.2783 13.2633C12.5194 13.2633 12.7427 13.202 12.9482 13.0795C13.1537 12.957 13.3177 12.7949 13.4403 12.5934C13.5628 12.3879 13.624 12.1626 13.624 11.9175C13.624 11.6725 13.5628 11.4492 13.4403 11.2476C13.3177 11.0421 13.1537 10.8781 12.9482 10.7556C12.7427 10.6331 12.5194 10.5718 12.2783 10.5718C12.0333 10.5718 11.808 10.6331 11.6025 10.7556C11.4009 10.8781 11.2369 11.0421 11.1104 11.2476C10.9879 11.4492 10.9266 11.6725 10.9266 11.9175C10.9266 12.1626 10.9879 12.3879 11.1104 12.5934C11.2369 12.7949 11.4009 12.957 11.6025 13.0795C11.808 13.202 12.0333 13.2633 12.2783 13.2633ZM7.2807 9.42763C6.88942 9.42763 6.53175 9.33278 6.20766 9.14307C5.88358 8.94941 5.62471 8.69251 5.43105 8.37238C5.24134 8.0483 5.14648 7.68865 5.14648 7.29342C5.14648 6.9061 5.24529 6.5504 5.4429 6.22632C5.64051 5.90223 5.90136 5.64336 6.22545 5.4497C6.54953 5.25604 6.90128 5.15921 7.2807 5.15921C7.67197 5.15921 8.02767 5.25604 8.3478 5.4497C8.67189 5.63941 8.93076 5.8963 9.12442 6.22039C9.31808 6.54052 9.41491 6.8982 9.41491 7.29342C9.41491 7.68865 9.31808 8.0483 9.12442 8.37238C8.93076 8.69251 8.67189 8.94941 8.3478 9.14307C8.02767 9.33278 7.67197 9.42763 7.2807 9.42763ZM7.2807 8.63916C7.52573 8.63916 7.74904 8.5779 7.9506 8.45538C8.15612 8.33286 8.32014 8.17082 8.44266 7.96925C8.56517 7.76374 8.62643 7.53846 8.62643 7.29342C8.62643 7.04838 8.56517 6.82508 8.44266 6.62352C8.32014 6.418 8.15612 6.25398 7.9506 6.13146C7.74904 6.00894 7.52573 5.94768 7.2807 5.94768C7.03566 5.94768 6.81038 6.00894 6.60486 6.13146C6.4033 6.25398 6.24126 6.418 6.11874 6.62352C5.99622 6.82508 5.93496 7.04838 5.93496 7.29342C5.93496 7.53846 5.99622 7.76374 6.11874 7.96925C6.24126 8.17082 6.4033 8.33286 6.60486 8.45538C6.81038 8.5779 7.03566 8.63916 7.2807 8.63916Z"
                                  fill="white"
                                />
                              </svg>`
                          : html``}

                        <div class="skio-price">
                          ${this.shouldShowComparePrice()
                            ? html`<span class="skio-compare-price">${this.compareAtPrice()}</span>`
                            : this.calculateDynamicDiscount(group.selected_selling_plan).percent !== '0%'
                            ? html`
                                <span class="skio-onetime-price" skio-onetime-price>
                                  ${this.moneyFormatter.format(this.selectedVariant.price / 100)}
                                </span>
                              `
                            : html``}
                          <span
                            class="skio-subscription-price"
                            style="color: ${this.subsPriceColor};"
                            skio-subscription-price
                          >
                            ${this.price(group.selected_selling_plan)}
                          </span>
                        </div>
                      </div>

                      ${this.specialOfferText
                        ? html`<span
                            class="skio-subscription-price-specialOfferText"
                            style="color: ${this.specialOfferColor}; background: ${this.specialOfferBgColor};"
                            >${this.specialOfferText}</span
                          >`
                        : html``}
                    </div>
                  </label>
                  <div class="sub-benefit-container  ${this.isCondensed == 'true' ? '--isCondensed' : ''}">
                    <ul class="sub-benefit-list">
                      <li>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                          <path
                            d="M8.375 13.6876L14.9844 7.07819L13.6719 5.76569L8.375 11.0626L5.70312 8.39069L4.39062 9.70319L8.375 13.6876ZM9.6875 18.7501C8.39062 18.7501 7.17188 18.504 6.03125 18.0118C4.89062 17.5196 3.89844 16.8516 3.05469 16.0079C2.21094 15.1641 1.54297 14.1719 1.05078 13.0313C0.558594 11.8907 0.3125 10.6719 0.3125 9.37506C0.3125 8.07819 0.558594 6.85944 1.05078 5.71881C1.54297 4.57819 2.21094 3.586 3.05469 2.74225C3.89844 1.8985 4.89062 1.23053 6.03125 0.738342C7.17188 0.246155 8.39062 6.10352e-05 9.6875 6.10352e-05C10.9844 6.10352e-05 12.2031 0.246155 13.3438 0.738342C14.4844 1.23053 15.4766 1.8985 16.3203 2.74225C17.1641 3.586 17.832 4.57819 18.3242 5.71881C18.8164 6.85944 19.0625 8.07819 19.0625 9.37506C19.0625 10.6719 18.8164 11.8907 18.3242 13.0313C17.832 14.1719 17.1641 15.1641 16.3203 16.0079C15.4766 16.8516 14.4844 17.5196 13.3438 18.0118C12.2031 18.504 10.9844 18.7501 9.6875 18.7501ZM9.6875 16.8751C11.7812 16.8751 13.5547 16.1485 15.0078 14.6954C16.4609 13.2422 17.1875 11.4688 17.1875 9.37506C17.1875 7.28131 16.4609 5.50787 15.0078 4.05475C13.5547 2.60162 11.7812 1.87506 9.6875 1.87506C7.59375 1.87506 5.82031 2.60162 4.36719 4.05475C2.91406 5.50787 2.1875 7.28131 2.1875 9.37506C2.1875 11.4688 2.91406 13.2422 4.36719 14.6954C5.82031 16.1485 7.59375 16.8751 9.6875 16.8751Z"
                            fill="#01415E"
                          /></svg
                        >Free shipping
                      </li>
                      <li>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                          <path
                            d="M8.375 13.6876L14.9844 7.07819L13.6719 5.76569L8.375 11.0626L5.70312 8.39069L4.39062 9.70319L8.375 13.6876ZM9.6875 18.7501C8.39062 18.7501 7.17188 18.504 6.03125 18.0118C4.89062 17.5196 3.89844 16.8516 3.05469 16.0079C2.21094 15.1641 1.54297 14.1719 1.05078 13.0313C0.558594 11.8907 0.3125 10.6719 0.3125 9.37506C0.3125 8.07819 0.558594 6.85944 1.05078 5.71881C1.54297 4.57819 2.21094 3.586 3.05469 2.74225C3.89844 1.8985 4.89062 1.23053 6.03125 0.738342C7.17188 0.246155 8.39062 6.10352e-05 9.6875 6.10352e-05C10.9844 6.10352e-05 12.2031 0.246155 13.3438 0.738342C14.4844 1.23053 15.4766 1.8985 16.3203 2.74225C17.1641 3.586 17.832 4.57819 18.3242 5.71881C18.8164 6.85944 19.0625 8.07819 19.0625 9.37506C19.0625 10.6719 18.8164 11.8907 18.3242 13.0313C17.832 14.1719 17.1641 15.1641 16.3203 16.0079C15.4766 16.8516 14.4844 17.5196 13.3438 18.0118C12.2031 18.504 10.9844 18.7501 9.6875 18.7501ZM9.6875 16.8751C11.7812 16.8751 13.5547 16.1485 15.0078 14.6954C16.4609 13.2422 17.1875 11.4688 17.1875 9.37506C17.1875 7.28131 16.4609 5.50787 15.0078 4.05475C13.5547 2.60162 11.7812 1.87506 9.6875 1.87506C7.59375 1.87506 5.82031 2.60162 4.36719 4.05475C2.91406 5.50787 2.1875 7.28131 2.1875 9.37506C2.1875 11.4688 2.91406 13.2422 4.36719 14.6954C5.82031 16.1485 7.59375 16.8751 9.6875 16.8751Z"
                            fill="#01415E"
                          /></svg
                        >2x Rewards Points
                      </li>

                      <li>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                          <path
                            d="M8.375 13.6876L14.9844 7.07819L13.6719 5.76569L8.375 11.0626L5.70312 8.39069L4.39062 9.70319L8.375 13.6876ZM9.6875 18.7501C8.39062 18.7501 7.17188 18.504 6.03125 18.0118C4.89062 17.5196 3.89844 16.8516 3.05469 16.0079C2.21094 15.1641 1.54297 14.1719 1.05078 13.0313C0.558594 11.8907 0.3125 10.6719 0.3125 9.37506C0.3125 8.07819 0.558594 6.85944 1.05078 5.71881C1.54297 4.57819 2.21094 3.586 3.05469 2.74225C3.89844 1.8985 4.89062 1.23053 6.03125 0.738342C7.17188 0.246155 8.39062 6.10352e-05 9.6875 6.10352e-05C10.9844 6.10352e-05 12.2031 0.246155 13.3438 0.738342C14.4844 1.23053 15.4766 1.8985 16.3203 2.74225C17.1641 3.586 17.832 4.57819 18.3242 5.71881C18.8164 6.85944 19.0625 8.07819 19.0625 9.37506C19.0625 10.6719 18.8164 11.8907 18.3242 13.0313C17.832 14.1719 17.1641 15.1641 16.3203 16.0079C15.4766 16.8516 14.4844 17.5196 13.3438 18.0118C12.2031 18.504 10.9844 18.7501 9.6875 18.7501ZM9.6875 16.8751C11.7812 16.8751 13.5547 16.1485 15.0078 14.6954C16.4609 13.2422 17.1875 11.4688 17.1875 9.37506C17.1875 7.28131 16.4609 5.50787 15.0078 4.05475C13.5547 2.60162 11.7812 1.87506 9.6875 1.87506C7.59375 1.87506 5.82031 2.60162 4.36719 4.05475C2.91406 5.50787 2.1875 7.28131 2.1875 9.37506C2.1875 11.4688 2.91406 13.2422 4.36719 14.6954C5.82031 16.1485 7.59375 16.8751 9.6875 16.8751Z"
                            fill="#01415E"
                          /></svg
                        >Cancel anytime
                      </li>
                      <li>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                          <path
                            d="M8.375 13.6876L14.9844 7.07819L13.6719 5.76569L8.375 11.0626L5.70312 8.39069L4.39062 9.70319L8.375 13.6876ZM9.6875 18.7501C8.39062 18.7501 7.17188 18.504 6.03125 18.0118C4.89062 17.5196 3.89844 16.8516 3.05469 16.0079C2.21094 15.1641 1.54297 14.1719 1.05078 13.0313C0.558594 11.8907 0.3125 10.6719 0.3125 9.37506C0.3125 8.07819 0.558594 6.85944 1.05078 5.71881C1.54297 4.57819 2.21094 3.586 3.05469 2.74225C3.89844 1.8985 4.89062 1.23053 6.03125 0.738342C7.17188 0.246155 8.39062 6.10352e-05 9.6875 6.10352e-05C10.9844 6.10352e-05 12.2031 0.246155 13.3438 0.738342C14.4844 1.23053 15.4766 1.8985 16.3203 2.74225C17.1641 3.586 17.832 4.57819 18.3242 5.71881C18.8164 6.85944 19.0625 8.07819 19.0625 9.37506C19.0625 10.6719 18.8164 11.8907 18.3242 13.0313C17.832 14.1719 17.1641 15.1641 16.3203 16.0079C15.4766 16.8516 14.4844 17.5196 13.3438 18.0118C12.2031 18.504 10.9844 18.7501 9.6875 18.7501ZM9.6875 16.8751C11.7812 16.8751 13.5547 16.1485 15.0078 14.6954C16.4609 13.2422 17.1875 11.4688 17.1875 9.37506C17.1875 7.28131 16.4609 5.50787 15.0078 4.05475C13.5547 2.60162 11.7812 1.87506 9.6875 1.87506C7.59375 1.87506 5.82031 2.60162 4.36719 4.05475C2.91406 5.50787 2.1875 7.28131 2.1875 9.37506C2.1875 11.4688 2.91406 13.2422 4.36719 14.6954C5.82031 16.1485 7.59375 16.8751 9.6875 16.8751Z"
                            fill="#01415E"
                          /></svg
                        >Get Exclusive Deals
                      </li>
                      <li class="sub-benefit-frequency"></li>
                    </ul>
                    <div class="skio-group-content">
                      <select
                        skio-selling-plans="${group.id}"
                        class="skio-frequency ${group.selling_plans.length == 1 ? 'skio-frequency--one' : ''}"
                        @change=${(e) => this.selectSellingPlan(e.target, group)}
                      >
                        ${group
                          ? group.selling_plans.map(
                              (selling_plan) =>
                                html`
                                  <option
                                    value="${selling_plan.id}"
                                    ?selected=${group.selected_selling_plan == selling_plan}
                                  >
                                    ${group.name == 'Subscription'
                                      ? `${selling_plan.name.toLowerCase()}`
                                      : `${selling_plan.name}`}
                                  </option>
                                `
                            )
                          : ''}
                      </select>
                    </div>
                  </div>
                </div>
              `
          )
        : ''}
    </fieldset>`;
  }

  updated = (changed) => {
    if (changed.has('product') && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) => selling_plan_group.app_id === 'SKIO'
      );

      this.skioSellingPlanGroups.forEach((group) => {
        group.selling_plans.sort(function (a, b) {
          if (parseInt(a.name.replace(/\D/g, '')) < parseInt(b.name.replace(/\D/g, ''))) return -1;
          if (parseInt(a.name.replace(/\D/g, '')) > parseInt(b.name.replace(/\D/g, ''))) return 1;
          if (parseInt(a.name.replace(/\D/g, '')) == parseInt(b.name.replace(/\D/g, ''))) return 0;
        });
      });
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      let skioSellingPlanGroups = JSON.parse(JSON.stringify(this.skioSellingPlanGroups));

      this.availableSellingPlanGroups = skioSellingPlanGroups.filter((selling_plan_group) =>
        selling_plan_group.selling_plans.some((selling_plan) =>
          this.selectedVariant.selling_plan_allocations.some(
            (selling_plan_allocation) => selling_plan_allocation.selling_plan_id === selling_plan.id
          )
        )
      );

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach((group) => {
          group.selling_plans = group.selling_plans.filter((x) =>
            this.selectedVariant.selling_plan_allocations.find((y) => y.selling_plan_id == x.id)
          );
        });

        this.availableSellingPlanGroups.forEach((group) => {
          if (this.defaultFrequency) {
            let selling_plan = group.selling_plans.find((x) =>
              x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase())
            );
            if (selling_plan) group.selected_selling_plan = selling_plan;
            else group.selected_selling_plan = group.selling_plans[0];
          } else {
            group.selected_selling_plan = group.selling_plans[0];
          }
        });

        if (
          this.startSubscription == true ||
          this.product.requires_selling_plan == true ||
          this.purchaseOption == 'subscription'
        ) {
          //find a matching selling plan, or choose first available

          if (this.selectedSellingPlan == null || this.selectedSellingPlan == undefined) {
            this.selectSellingPlanGroup(this.availableSellingPlanGroups[0]);
          }

          let sellingPlanName = this.selectedSellingPlan?.name;

          let sellingPlanGroup = this.availableSellingPlanGroups.find((x) =>
            x.selling_plans.find((y) => y.name == sellingPlanName)
          );
          let sellingPlan = sellingPlanGroup?.selling_plans.find((y) => y.name == sellingPlanName);
          if (sellingPlanName == sellingPlan?.name) {
            this.selectedSellingPlanGroup = sellingPlanGroup;
            this.selectedSellingPlan = sellingPlan;
          } else {
            this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
            this.selectedSellingPlan = this.availableSellingPlanGroups[0].selling_plans[0];
          }
        } else {
          this.selectedSellingPlan, (this.selectedSellingPlanGroup = null);
        }
      } else {
        this.selectedSellingPlan, (this.selectedSellingPlanGroup = null);
      }

      //update the form that was passed, if any
      this.updateForm();
    }

    if (changed.has('selectedSellingPlan')) {
      //update price of price elements if applicable
      document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach((el) => {
        el.innerHTML = this.price(this.selectedSellingPlan);
      });

      //update display of external content elements
      document.querySelectorAll(`[skio-onetime-content][skio-key="${this.key}"]`).forEach((el) => {
        this.selectedSellingPlan !== null ? (el.style.display = 'none') : el.style.removeProperty('display');
      });

      document.querySelectorAll(`[skio-subscription-content][skio-key="${this.key}"]`).forEach((el) => {
        this.selectedSellingPlan == null ? (el.style.display = 'none') : el.style.removeProperty('display');
      });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      });

      this.dispatchEvent(event);

      //update the form that was passed, if any
      this.updateForm();
      this.updateExternalPrice();

      //update external selling_plan input value
      let sellingPlanInput = document.querySelector('input[name="selling_plan"]');
      this.selectedSellingPlan != null
        ? (sellingPlanInput.value = this.selectedSellingPlan.id)
        : (sellingPlanInput.value = '');
    }

    if (changed.has('formId')) {
      //update the form that was passed, if any
      this.updateForm();
    }
  };

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;');
    console.log.apply(console, args);
  };

  error = (...args) => {
    args.unshift('%c [skio]', 'color: #ff0000');
    console.error.apply(console, args);
  };

  updateExternalPrice() {
    document.querySelectorAll('dd:not(.price__compare) .price-item bdi').forEach((el) => {
      el.innerHTML = this.price(this.selectedSellingPlan);
    });
    document.querySelectorAll(this.externalPriceSelectorWithCurrency).forEach((el) => {
      el.innerHTML = this.price(this.selectedSellingPlan) + ' ' + this.currency;
    });
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    if (this.selectedSellingPlan) this.lastSellingPlanName = this.selectedSellingPlan.name;
    if (group) this.purchaseOption = 'subscription';
    else this.purchaseOption = 'onetime';

    //update the form that was passed, if any
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find((x) => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          ' for variant ' +
          this.selectedVariant.id +
          ' from product ' +
          this.product.id +
          ' : ' +
          this.product.handle
      );
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlanButton(plan, group) {
    let selling_plan = group.selling_plans.find((x) => x.id == plan.id);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          ' for variant ' +
          this.selectedVariant.id +
          ' from product ' +
          this.product.id +
          ' : ' +
          this.product.handle
      );
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0);
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan) return { percent: '0%', amount: 0 };

    const price_adjustment = selling_plan.price_adjustments[0];
    const discount = { percent: '0%', amount: 0 };
    const price = this.selectedVariant.price;

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`;
        discount.amount = Math.round((price * price_adjustment.value) / 100.0);
        break;
      case 'fixed_amount':
        discount.percent = `${Math.round(((price_adjustment.value * 1.0) / price) * 100.0)}%`;
        discount.amount = price_adjustment.value;
        break;
      case 'price':
        discount.percent = `${Math.round((((price - price_adjustment.value) * 1.0) / price) * 100.0)}%`;
        discount.amount = price - price_adjustment.value;
        break;
    }

    return discount;
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    return formatted
      ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount)
      : this.selectedVariant.price - this.discount(selling_plan).amount;
  }

  // Gets the compare-at-price for the current variant, returns formatted money value
  compareAtPrice(formatted = true) {
    if (!this.selectedVariant.compare_at_price || this.selectedVariant.compare_at_price <= this.selectedVariant.price) {
      return null;
    }
    return formatted ? this.money(this.selectedVariant.compare_at_price) : this.selectedVariant.compare_at_price;
  }

  // Checks if compare-at-price should be shown
  shouldShowComparePrice() {
    return this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > this.selectedVariant.price;
  }

  // Calculates dynamic discount percentage between compare-at-price and final price
  calculateDynamicDiscount(selling_plan = null) {
    if (!this.shouldShowComparePrice()) {
      const fallbackDiscount = this.discount(selling_plan);
      return {
        percent: fallbackDiscount.percent,
        amount: this.money(fallbackDiscount.amount),
      };
    }

    const comparePrice = this.selectedVariant.compare_at_price;
    const finalPrice = selling_plan
      ? this.selectedVariant.price - this.discount(selling_plan).amount
      : this.selectedVariant.price;

    const discountAmount = comparePrice - finalPrice;
    const discountPercent = Math.round((discountAmount / comparePrice) * 100);

    return {
      percent: `${discountPercent}%`,
      amount: this.money(discountAmount),
    };
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm() {
    if (!this.formId) return;

    const targetFormIds = [this.formId, `${this.formId}--alt`];

    targetFormIds.forEach((id) => {
      const form = document.querySelector(`#${id}`);
      if (!form) return;

      let selling_plan_input = form.querySelector('[name="selling_plan"]');
      if (selling_plan_input) {
        selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
        selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
      } else {
        selling_plan_input = document.createElement('input');
        selling_plan_input.type = 'hidden';
        selling_plan_input.name = 'selling_plan';
        selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
        selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        form.append(selling_plan_input);
      }

      let discount_input = form.querySelector('[name="properties[Discount]"]');
      if (discount_input) {
        discount_input.value =
          this.selectedSellingPlan?.id !== undefined
            ? this.calculateDynamicDiscount(this.selectedSellingPlan).percent
            : null;
        discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
      } else {
        discount_input = document.createElement('input');
        discount_input.type = 'hidden';
        discount_input.name = 'properties[Discount]';
        discount_input.value =
          this.selectedSellingPlan?.id !== undefined
            ? this.calculateDynamicDiscount(this.selectedSellingPlan).percent
            : null;
        discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        form.append(discount_input);
      }
    });
  }

  addVariantClickEventListeners() {
    let variantInputs = document.querySelectorAll(this.variantInputSelector);
    let skio = this;
    for (let el of variantInputs) {
      el.addEventListener('click', function (e) {
        // may need to replace with ID / e.target depending on client setup
        // may need to use different attribute depending on ^^
        let variantTitle = e.currentTarget.value;
        skio.selectedVariant = skio.product.variants.find((variant) => variant.title == variantTitle);
      });
    }
  }

  // Optional functions keep if necessary

  /**
   *
   *
   */

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id }),
      },
    ];

    // Trigger TriplePixel and log the message
    window.TriplePixel?.('AddToCart', { item: this.selectedVariant.id, q: quantity });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        this.log('SKIO added item to cart: ', response);
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        });

        this.dispatchEvent(event);
      })
      .catch((error) => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error);
      });
  }

  fetchProduct = (handle) => {
    return fetch(`/products/${handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        this.product = product;
        this.selectedVariant = product.variants[0];

        return product;
      });
  };
}

customElements.define('skio-plan-picker', SkioPlanPickerComponent);
